import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Award,
  BookOpen,
  Clock,
  PlayCircle,
  CheckCircle,
  AlertCircle,
  MapPin,
  Search,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { trackingService } from '@/services/tracking.service';
import { certificationService, UserCertificationSummaryDto } from '@/services/certification.service';
import { learnerLearningPathService, LearningPathWithProgress } from '@/services/learner-learning-path.service';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PERMISSION_LIST_KEY,
  LEARNER_COURSES_PERMISSION,
  LEARNER_CERTIFICATIONS_PERMISSION,
  LEARNER_LEARNING_PATHS_PERMISSION,
} from '@/constants/constant-permissions';

interface CourseSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CourseWithProgress {
  course_id: string;
  course_title: string;
  description: string;
  difficulty: string;
  stage: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  estimated_duration: {
    hours: number;
    minutes: number;
    total_minutes: number;
  };
  rating?: number;
  assignment_id?: string;
  createdAt: string;
  updatedAt: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  progress_percentage: number;
  is_mandatory: boolean;
  deadline_date?: string;
  last_accessed_at?: string;
  total_time_spent_seconds: number;
  completed_modules: number;
  total_modules: number;
}

type TabType = 'courses' | 'certifications' | 'learning_paths';

export default function CourseSearchModal({ isOpen, onClose }: CourseSearchModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('courses');
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Permissions
  // Permissions — tabs are hidden if user lacks write permission
  const permissions = useMemo(() => {
    const p: string[] = JSON.parse(sessionStorage.getItem(PERMISSION_LIST_KEY) || '[]');
    return {
      canAccessCourses: p.includes(LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE),
      canAccessCertifications: p.includes(LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_WRITE),
      canAccessLearningPaths: p.includes(LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_WRITE),
    };
  }, [isOpen]);

  const allTabs = [
    { id: 'courses' as TabType, label: 'Courses', icon: <BookOpen className="w-4 h-4" />, visible: permissions.canAccessCourses },
    { id: 'certifications' as TabType, label: 'Certifications', icon: <Award className="w-4 h-4" />, visible: permissions.canAccessCertifications },
    { id: 'learning_paths' as TabType, label: 'Learning Paths', icon: <MapPin className="w-4 h-4" />, visible: permissions.canAccessLearningPaths },
  ];
  const tabs = allTabs.filter(t => t.visible);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      if (tabs.length > 0) setActiveTab(tabs[0].id);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Fetch data
  const { data: courses, isLoading: coursesLoading } = useQuery<CourseWithProgress[]>({
    queryKey: ['learner-assigned-courses'],
    queryFn: () => trackingService.getMyCourses(),
    enabled: isOpen && activeTab === 'courses',
  });

  const { data: certifications, isLoading: certificationsLoading } = useQuery<UserCertificationSummaryDto[]>({
    queryKey: ['learner-my-certifications'],
    queryFn: () => certificationService.getUserCertifications(),
    enabled: isOpen && activeTab === 'certifications',
  });

  const { data: learningPaths, isLoading: learningPathsLoading } = useQuery<LearningPathWithProgress[]>({
    queryKey: ['learner-learning-paths'],
    queryFn: () => learnerLearningPathService.getMyLearningPaths(),
    enabled: isOpen && activeTab === 'learning_paths',
  });

  // Filter results
  const filteredResults = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();

    if (activeTab === 'courses') {
      const list = courses || [];
      if (!query) return list;
      return list.filter(c =>
        c.course_title.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query) ||
        c.difficulty?.toLowerCase().includes(query)
      );
    }

    if (activeTab === 'certifications') {
      const list = certifications || [];
      if (!query) return list;
      return list.filter(c =>
        c.title.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query)
      );
    }

    if (activeTab === 'learning_paths') {
      const list = learningPaths || [];
      if (!query) return list;
      return list.filter(lp =>
        lp.title.toLowerCase().includes(query) ||
        lp.description?.toLowerCase().includes(query)
      );
    }

    return [];
  }, [activeTab, searchQuery, courses, certifications, learningPaths]);

  const isLoading = activeTab === 'courses' ? coursesLoading
    : activeTab === 'certifications' ? certificationsLoading
    : learningPathsLoading;

  // Handle click
  const handleResultClick = (result: any) => {
    if (activeTab === 'courses') {
      navigate(`/learn/study/${result.course_id}`);
      toast.success(`Opening ${result.course_title}`);
    } else if (activeTab === 'certifications') {
      navigate(`/learn/certification/${result.certification_id}`);
    } else if (activeTab === 'learning_paths') {
      navigate(`/learn/learning-paths/${result.learning_path_id}`);
    }
    onClose();
  };

  // Keyboard
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Status display helpers
  const getStatusDisplay = (status: string, progress?: number) => {
    switch (status) {
      case 'COMPLETED':
        return { icon: <CheckCircle className="w-3.5 h-3.5 text-green-600" />, text: 'Completed', color: 'text-green-600' };
      case 'IN_PROGRESS':
        return { icon: <PlayCircle className="w-3.5 h-3.5 text-blue-600" />, text: `${Math.round(progress || 0)}%`, color: 'text-blue-600' };
      case 'EXPIRED':
        return { icon: <AlertCircle className="w-3.5 h-3.5 text-red-600" />, text: 'Expired', color: 'text-red-600' };
      default:
        return { icon: <BookOpen className="w-3.5 h-3.5 text-gray-500" />, text: 'Not Started', color: 'text-gray-500' };
    }
  };

  const formatDuration = (duration: { hours: number; minutes: number }) => {
    if (duration.hours > 0) return duration.minutes > 0 ? `${duration.hours}h ${duration.minutes}m` : `${duration.hours}h`;
    return `${duration.minutes}m`;
  };

  if (!isOpen || tabs.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Search</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder={`Search ${tabs.find(t => t.id === activeTab)?.label?.toLowerCase() || 'items'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Tabs */}
        {tabs.length > 1 && (
          <div className="flex border-b border-gray-200 bg-gray-50">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); inputRef.current?.focus(); }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-primary-700 bg-white border-b-2 border-primary-500'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-gray-600">Loading...</span>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="text-center py-8">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">
                {searchQuery ? 'No results found' : `No ${tabs.find(t => t.id === activeTab)?.label?.toLowerCase() || 'items'} assigned`}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {searchQuery ? 'Try a different search term' : 'Check back later for new assignments'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredResults.map((result: any) => {
                const isCourse = activeTab === 'courses';
                const isCert = activeTab === 'certifications';
                const isLP = activeTab === 'learning_paths';
                const title = isCourse ? result.course_title : result.title;
                const id = isCourse ? result.course_id : isCert ? result.certification_id : result.learning_path_id;
                const status = getStatusDisplay(result.status, result.progress_percentage);
                const isOverdue = result.deadline_date && new Date(result.deadline_date) < new Date();

                return (
                  <button
                    key={id}
                    onClick={() => handleResultClick(result)}
                    className="w-full p-3 border border-gray-200 rounded-lg text-left transition-all hover:border-primary-300 hover:shadow-md cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      {/* Thumbnail */}
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {result.thumbnail_image_url || result.banner_image_url ? (
                          <img
                            src={result.thumbnail_image_url || result.banner_image_url}
                            alt={title}
                            className="w-full h-full object-cover"
                          />
                        ) : isCourse ? (
                          <BookOpen className="w-5 h-5 text-primary-400" />
                        ) : isCert ? (
                          <Award className="w-5 h-5 text-amber-500" />
                        ) : (
                          <MapPin className="w-5 h-5 text-emerald-500" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-medium text-gray-900 text-sm truncate">{title}</h3>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {result.is_mandatory && (
                              <span className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">Mandatory</span>
                            )}
                            {isOverdue && (
                              <span className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">Overdue</span>
                            )}
                          </div>
                        </div>

                        {result.description && (
                          <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{result.description}</p>
                        )}

                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                          {isCourse && result.estimated_duration && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDuration(result.estimated_duration)}
                            </span>
                          )}
                          {isCourse && result.difficulty && (
                            <span className="capitalize">{result.difficulty}</span>
                          )}
                          {isLP && (
                            <span>{result.total_courses} courses</span>
                          )}
                          {isCert && result.time_limit_minutes && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {result.time_limit_minutes} min
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            {status.icon}
                            <span className={status.color}>{status.text}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            {filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''} shown 
          </p>
        </div>
      </div>
    </div>
  );
}
